import { Copy, Download, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

/**
 * Clean Call Summary Display
 *
 * Shows all collected answers from a call in a simple, easy-to-read list format.
 * Designed to be copied/pasted into other tools without looking technical.
 */

export interface SummaryEntry {
  question: string;
  answer: string;
}

export interface CallSummaryDisplayProps {
  flowTitle: string;
  completedAt: number;
  entries: SummaryEntry[];
  outcome: string;
  onDismiss: () => void;
}

export function CallSummaryDisplay({
  flowTitle,
  completedAt,
  entries,
  outcome,
  onDismiss,
}: CallSummaryDisplayProps) {
  // Generate clean text for copy-paste
  const generateSummaryText = () => {
    const lines = [
      flowTitle,
      new Date(completedAt).toLocaleString(),
      "━".repeat(50),
    ];

    if (entries.length > 0) {
      entries.forEach((entry) => {
        lines.push(`${entry.question}`);
        lines.push(`${entry.answer}`);
        lines.push(""); // Blank line between entries
      });
    } else {
      lines.push("No answers recorded");
    }

    lines.push("━".repeat(50));
    lines.push(`Outcome: ${outcome}`);

    return lines.join("\n");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generateSummaryText());
      toast.success("Summary copied to clipboard!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleDownload = () => {
    const text = generateSummaryText();
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const slug = flowTitle
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "call-summary";
    anchor.href = url;
    anchor.download = `${slug}-${completedAt}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-slate-900">Call Summary</h2>
          <p className="text-xs text-slate-600 mt-1">
            {new Date(completedAt).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            className="text-xs"
          >
            <Copy className="h-3.5 w-3.5 mr-1" />
            Copy
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownload}
            className="text-xs"
          >
            <Download className="h-3.5 w-3.5 mr-1" />
            Download
          </Button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded p-1 text-slate-400 hover:bg-white hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Answers List */}
      <div className="space-y-4">
        {entries.length === 0 ? (
          <p className="text-sm text-slate-600">No answers recorded</p>
        ) : (
          entries.map((entry, idx) => (
            <div key={idx} className="flex gap-4">
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">
                  {entry.question}
                </div>
                <div className="text-sm text-slate-700 mt-1 leading-relaxed">
                  {entry.answer}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-green-200">
        <div className="text-sm">
          <span className="font-semibold text-slate-900">Outcome: </span>
          <span className="text-slate-700">{outcome}</span>
        </div>
      </div>

      {/* Plain Text Preview (for understanding what gets copied) */}
      <details className="mt-4 pt-4 border-t border-green-200">
        <summary className="text-xs font-semibold text-slate-600 cursor-pointer hover:text-slate-900">
          View as Text (for copy-paste)
        </summary>
        <pre className="mt-2 p-3 bg-white rounded border border-slate-200 text-xs text-slate-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
          {generateSummaryText()}
        </pre>
      </details>
    </div>
  );
}
